import { useState, useRef, useEffect } from 'react';
import { TreePine } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { login, getWebauthnLoginOptions, verifyWebauthnLogin } from '@/api/auth.api';
import { useAuthStore } from '@/store/useAuthStore';
import { useTexte } from '@/hooks/useTexte';
import type { LoginResponse, TwoFactorRequiredResponse } from '@/api/auth.api';

type TwoFactorMethod = 'totp' | 'webauthn' | null;

export function LoginPage() {
  const { text } = useTexte();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { message?: string; from?: { pathname?: string } } | null;
  const successMessage = locationState?.message;
  // Ursprünglich angeforderte URL (vom AuthGuard hinterlegt) – nach Login dorthin
  const redirectTo = locationState?.from?.pathname ?? '/';
  const queryClient = useQueryClient();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  /**
   * Ersatzcode statt Authenticator-Code.
   *
   * Dasselbe Feld, andere Form: sechs Ziffern aus der App oder zehn Zeichen
   * vom Zettel. Wer sein Telefon verloren hat, findet den Weg sonst nicht –
   * und das ist genau der Moment, in dem er ihn braucht.
   */
  const [mitErsatzcode, setMitErsatzcode] = useState(false);
  const ersatzcodeSauber = totpCode.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const codeVollstaendig = mitErsatzcode
    ? ersatzcodeSauber.length === 10
    : totpCode.length === 6;
  const [loading, setLoading] = useState(false);
  const [requires2fa, setRequires2fa] = useState(false);
  const [available2faMethods, setAvailable2faMethods] = useState<{ totp: boolean; webauthn: boolean }>({ totp: false, webauthn: false });
  const [selectedMethod, setSelectedMethod] = useState<TwoFactorMethod>(null);
  const totpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectedMethod === 'totp' && totpInputRef.current) {
      totpInputRef.current.focus();
    }
  }, [selectedMethod]);

  const handleLoginSuccess = (result: LoginResponse) => {
    setAuth(result.accessToken, result.user, result.csrfToken);
    queryClient.removeQueries();
    if (result.user.mustChangePassword) {
      navigate('/change-password', { replace: true });
    } else {
      navigate(redirectTo, { replace: true });
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password, selectedMethod === 'totp' ? totpCode : undefined);

      if ('requiresTwoFactor' in result && (result as TwoFactorRequiredResponse).requiresTwoFactor) {
        const r = result as TwoFactorRequiredResponse;
        setRequires2fa(true);
        setAvailable2faMethods(r.methods);

        // Wenn nur eine Methode verfügbar ist, direkt auswählen
        if (r.methods.totp && !r.methods.webauthn) {
          setSelectedMethod('totp');
        } else if (r.methods.webauthn && !r.methods.totp) {
          setSelectedMethod('webauthn');
        } else {
          // Beide verfügbar – User wählen lassen
          setSelectedMethod(null);
        }

        setLoading(false);
        return;
      }

      const erfolg = result as LoginResponse;
      if (erfolg.ersatzcodeBenutzt) {
        // Nicht bloß „angemeldet": ein verbrauchter Ersatzcode ist einer
        // weniger, und wer das nicht mitbekommt, steht beim letzten wieder da,
        // wo er angefangen hat.
        toast(
          erfolg.ersatzcodeRest === 0
            ? 'Das war dein letzter Ersatzcode. Bitte erzeuge unter „Sicherheit" neue.'
            : `Ersatzcode verbraucht – noch ${erfolg.ersatzcodeRest} übrig.`,
          { icon: '🔑', duration: 8000 },
        );
      }
      handleLoginSuccess(erfolg);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login fehlgeschlagen.';
      toast.error(message);
      if (selectedMethod === 'totp') setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleWebAuthnLogin = async () => {
    setLoading(true);
    try {
      const options = await getWebauthnLoginOptions(email);
      const credential = await navigator.credentials.get({ publicKey: options }) as PublicKeyCredential;
      if (!credential) throw new Error('Kein Credential erhalten.');

      const result = await verifyWebauthnLogin(email, credential);
      handleLoginSuccess(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'WebAuthn-Login fehlgeschlagen.';
      // NotAllowedError = User hat abgebrochen, kein Toast
      if (err instanceof Error && err.name !== 'NotAllowedError') {
        toast.error(message);
      }
      setLoading(false);
    }
  };

  const handleSelectMethod = (method: 'totp' | 'webauthn') => {
    setSelectedMethod(method);
    if (method === 'webauthn') {
      handleWebAuthnLogin();
    }
  };

  const handleBack = () => {
    setRequires2fa(false);
    setSelectedMethod(null);
    setTotpCode('');
  };

  const handleBackToMethodSelection = () => {
    setSelectedMethod(null);
    setTotpCode('');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <TreePine className="mx-auto h-10 w-10 text-emerald-600" aria-hidden="true" />
          <h1 className="mt-2 text-2xl font-bold text-slate-900">{text('app.name')}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {!requires2fa ? 'Bitte melde dich an' : 'Zwei-Faktor-Authentifizierung'}
          </p>
          {text('app.tagline') && (
            <p className="mt-0.5 text-xs text-slate-400">{text('app.tagline')}</p>
          )}
        </div>

        {successMessage && (
          <div className="mb-4 rounded-md bg-green-50 p-3 text-sm text-green-700">
            {successMessage}
          </div>
        )}

        {/* ── Schritt 1: E-Mail + Passwort ─────────────────────── */}
        {!requires2fa && (
          <form onSubmit={handleLogin} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-1 block text-sm font-medium text-slate-700">E-Mail</label>
                <input
                  id="email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required autoFocus autoComplete="email"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="admin@stammbaum.local"
                />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700">Passwort</label>
                  <Link to="/forgot-password" className="text-xs text-blue-600 hover:text-blue-700">
                    Passwort vergessen?
                  </Link>
                </div>
                <input
                  id="password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required autoComplete="current-password"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="mt-6 flex w-full items-center justify-center rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              ) : 'Anmelden'}
            </button>
          </form>
        )}

        {/* ── Schritt 2a: Methodenauswahl (beide verfügbar) ──────── */}
        {requires2fa && selectedMethod === null && (
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm space-y-4">
            <p className="text-sm text-slate-600">Wähle deine Verifizierungsmethode:</p>

            {available2faMethods.webauthn && window.PublicKeyCredential && (
              <button
                type="button"
                onClick={() => handleSelectMethod('webauthn')}
                disabled={loading}
                className="flex w-full items-center gap-3 rounded-md border border-slate-300 bg-white px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100">
                  <svg className="h-5 w-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.864 4.243A7.5 7.5 0 0 1 19.5 10.5c0 2.92-.556 5.709-1.568 8.268M5.742 6.364A7.465 7.465 0 0 0 4.5 10.5a7.464 7.464 0 0 1-1.15 3.993m1.989 3.559A11.209 11.209 0 0 0 8.25 10.5a3.75 3.75 0 1 1 7.5 0c0 .527-.021 1.049-.064 1.565M12 10.5a14.94 14.94 0 0 1-3.6 9.75m6.633-4.596a18.666 18.666 0 0 1-2.485 5.33" />
                  </svg>
                </span>
                <div>
                  <div className="font-medium">YubiKey / Passkey</div>
                  <div className="text-xs text-slate-400">Sicherheitsschlüssel oder Gerät berühren</div>
                </div>
              </button>
            )}

            {available2faMethods.totp && (
              <button
                type="button"
                onClick={() => handleSelectMethod('totp')}
                disabled={loading}
                className="flex w-full items-center gap-3 rounded-md border border-slate-300 bg-white px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100">
                  <svg className="h-5 w-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 8.25h3m-3 3.75h3m-3 3.75h3" />
                  </svg>
                </span>
                <div>
                  <div className="font-medium">Authenticator-App</div>
                  <div className="text-xs text-slate-400">6-stelliger Code aus deiner App</div>
                </div>
              </button>
            )}

            <button type="button" onClick={handleBack} className="text-sm text-slate-500 hover:text-slate-700">
              &larr; Zurück zum Login
            </button>
          </div>
        )}

        {/* ── Schritt 2b: TOTP-Eingabe ────────────────────────────── */}
        {requires2fa && selectedMethod === 'totp' && (
          <form onSubmit={handleLogin} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm space-y-4">
            <div className="rounded-md bg-blue-50 p-3">
              <p className="text-sm text-blue-700">
                {mitErsatzcode
                  ? 'Gib einen deiner Ersatzcodes ein. Jeder gilt genau einmal.'
                  : 'Gib den 6-stelligen Code aus deiner Authenticator-App ein.'}
              </p>
            </div>
            <div>
              <label htmlFor="totpCode" className="mb-1 block text-sm font-medium text-slate-700">
                {mitErsatzcode ? 'Ersatzcode' : 'Zwei-Faktor-Code'}
              </label>
              {mitErsatzcode ? (
                <input
                  ref={totpInputRef} id="totpCode" type="text"
                  maxLength={11}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                  required autoComplete="one-time-code" autoCapitalize="characters" spellCheck={false}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-center text-lg font-mono tracking-[0.2em] text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="ABCDE-FGHJK"
                />
              ) : (
                <input
                  ref={totpInputRef} id="totpCode" type="text"
                  inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  required autoComplete="one-time-code"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-center text-lg font-mono tracking-[0.5em] text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="000000"
                />
              )}
              <button
                type="button"
                onClick={() => { setMitErsatzcode(!mitErsatzcode); setTotpCode(''); }}
                className="mt-2 text-sm text-blue-600 hover:underline"
              >
                {mitErsatzcode
                  ? 'Doch Code aus der App eingeben'
                  : 'Kein Zugriff auf die App? Ersatzcode verwenden'}
              </button>
            </div>
            <button
              type="submit"
              disabled={loading || !codeVollstaendig}
              className="flex w-full items-center justify-center rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              ) : 'Bestätigen'}
            </button>
            {available2faMethods.webauthn && available2faMethods.totp && (
              <button type="button" onClick={handleBackToMethodSelection} className="text-sm text-slate-500 hover:text-slate-700">
                &larr; Andere Methode wählen
              </button>
            )}
            {!available2faMethods.webauthn && (
              <button type="button" onClick={handleBack} className="text-sm text-slate-500 hover:text-slate-700">
                &larr; Zurück zum Login
              </button>
            )}
          </form>
        )}

        {/* ── Schritt 2c: WebAuthn-Wartescreen ─────────────────────── */}
        {requires2fa && selectedMethod === 'webauthn' && (
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm space-y-4">
            {loading ? (
              <div className="flex flex-col items-center gap-4 py-4">
                <span className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
                <p className="text-sm text-slate-600">Bitte YubiKey berühren oder Passkey bestätigen…</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-600">Starte den YubiKey / Passkey erneut:</p>
                <button
                  type="button"
                  onClick={handleWebAuthnLogin}
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.864 4.243A7.5 7.5 0 0 1 19.5 10.5c0 2.92-.556 5.709-1.568 8.268M5.742 6.364A7.465 7.465 0 0 0 4.5 10.5a7.464 7.464 0 0 1-1.15 3.993m1.989 3.559A11.209 11.209 0 0 0 8.25 10.5a3.75 3.75 0 1 1 7.5 0c0 .527-.021 1.049-.064 1.565M12 10.5a14.94 14.94 0 0 1-3.6 9.75m6.633-4.596a18.666 18.666 0 0 1-2.485 5.33" />
                  </svg>
                  YubiKey / Passkey verwenden
                </button>
              </>
            )}
            {available2faMethods.webauthn && available2faMethods.totp && !loading && (
              <button type="button" onClick={handleBackToMethodSelection} className="text-sm text-slate-500 hover:text-slate-700">
                &larr; Andere Methode wählen
              </button>
            )}
            {!available2faMethods.totp && !loading && (
              <button type="button" onClick={handleBack} className="text-sm text-slate-500 hover:text-slate-700">
                &larr; Zurück zum Login
              </button>
            )}
          </div>
        )}
        {(text('login.hint') || text('login.supportContact')) && (
          <div className="mt-6 space-y-1 text-center text-xs text-slate-400">
            {text('login.hint') && <p>{text('login.hint')}</p>}
            {text('login.supportContact') && <p>{text('login.supportContact')}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
